import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  Mail,
  Phone,
  FileText,
  Clock,
  Send,
  MessageSquare,
  ChevronRight,
  BarChart3,
  Plus,
  RefreshCw,
  Eye,
  MoreHorizontal,
  Calendar,
} from 'lucide-react';
import { EmailComposer } from './EmailComposer';
import { CallLogger } from './CallLogger';
import { NotesEditor } from './NotesEditor';
import { ActivityHistory } from './ActivityHistory';
import { EmailTemplates } from './EmailTemplates';
import type { Contact } from '@/types/contacts';

interface CommunicationHubProps {
  contact: Contact;
  onRefresh?: () => void;
}

interface CommunicationStats {
  emails: {
    total: number;
    sent: number;
    drafts: number;
    opened: number;
    openRate: number;
  };
  calls: {
    total: number;
    completed: number;
    avgDuration: number;
  };
  notes: {
    total: number;
  };
}

export function CommunicationHub({ contact, onRefresh }: CommunicationHubProps) {
  const [activeTab, setActiveTab] = useState('activity');
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [showCallLogger, setShowCallLogger] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [stats, setStats] = useState<CommunicationStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await fetch(`/api/crm/communications/stats/${contact.id}`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [contact.id]);

  const handleEmailSent = () => {
    setShowEmailComposer(false);
    fetchStats();
    onRefresh?.();
  };

  const handleCallLogged = () => {
    setShowCallLogger(false);
    fetchStats();
    onRefresh?.();
  };

  const handleTemplateSelect = (template: { subject: string; body: string }) => {
    setShowTemplates(false);
    setShowEmailComposer(true);
  };

  const statCards = [
    {
      label: 'Emails',
      icon: Mail,
      value: stats?.emails.total || 0,
      subValue: stats ? `${stats.emails.openRate}% open rate` : '-',
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-500/10',
    },
    {
      label: 'Calls',
      icon: Phone,
      value: stats?.calls.total || 0,
      subValue: stats ? `${Math.floor((stats.calls.avgDuration || 0) / 60)}m avg` : '-',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-500/10',
    },
    {
      label: 'Notes',
      icon: FileText,
      value: stats?.notes.total || 0,
      subValue: 'Total notes',
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-500/10',
    },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header with Contact Info */}
      <Card className="border-0 shadow-none bg-transparent">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-14 w-14 ring-2 ring-[#2B7FFF]/20">
                <AvatarImage src={contact.avatar} alt={contact.name} />
                <AvatarFallback className="bg-[#2B7FFF]/10 text-[#2B7FFF] text-lg">
                  {contact.name.split(' ').map((n) => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-lg">{contact.name}</CardTitle>
                <CardDescription className="flex items-center gap-2">
                  {contact.title && <span>{contact.title}</span>}
                  {contact.company && (
                    <>
                      <span>•</span>
                      <span>{contact.company}</span>
                    </>
                  )}
                </CardDescription>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary" className="text-xs">
                    {contact.stage}
                  </Badge>
                  {contact.email && (
                    <span className="text-xs text-muted-foreground">{contact.email}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTemplates(true)}
                className="hidden sm:flex"
              >
                <Mail className="h-4 w-4 mr-2" />
                Templates
              </Button>
              <Button
                size="sm"
                onClick={() => setShowEmailComposer(true)}
                className="bg-[#2B7FFF] hover:bg-[#1A5FCC]"
              >
                <Send className="h-4 w-4 mr-2" />
                Email
              </Button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            {statCards.map((stat) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg border',
                  'bg-card hover:bg-accent/50 transition-colors'
                )}
              >
                <div className={cn('p-2 rounded-md', stat.bgColor)}>
                  <stat.icon className={cn('h-4 w-4', stat.color)} />
                </div>
                <div>
                  <div className="text-2xl font-semibold">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.subValue}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </CardHeader>
      </Card>

      <Separator />

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <div className="px-6 py-3 border-b">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="activity" className="gap-2">
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">Activity</span>
            </TabsTrigger>
            <TabsTrigger value="emails" className="gap-2">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">Emails</span>
            </TabsTrigger>
            <TabsTrigger value="calls" className="gap-2">
              <Phone className="h-4 w-4" />
              <span className="hidden sm:inline">Calls</span>
            </TabsTrigger>
            <TabsTrigger value="notes" className="gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Notes</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-hidden">
          <TabsContent value="activity" className="h-full mt-0">
            <ScrollArea className="h-full">
              <div className="p-6">
                <ActivityHistory contactId={contact.id} />
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="emails" className="h-full mt-0">
            <EmailList contactId={contact.id} onCompose={() => setShowEmailComposer(true)} />
          </TabsContent>

          <TabsContent value="calls" className="h-full mt-0">
            <CallList contactId={contact.id} onLogCall={() => setShowCallLogger(true)} />
          </TabsContent>

          <TabsContent value="notes" className="h-full mt-0">
            <ScrollArea className="h-full">
              <div className="p-6">
                <NotesEditor contactId={contact.id} onNoteSaved={fetchStats} />
              </div>
            </ScrollArea>
          </TabsContent>
        </div>
      </Tabs>

      {/* Modals */}
      <AnimatePresence>
        {showEmailComposer && (
          <EmailComposer
            contact={contact}
            onClose={() => setShowEmailComposer(false)}
            onSent={handleEmailSent}
          />
        )}
        {showCallLogger && (
          <CallLogger
            contact={contact}
            onClose={() => setShowCallLogger(false)}
            onLogged={handleCallLogged}
          />
        )}
        {showTemplates && (
          <EmailTemplates
            onSelect={handleTemplateSelect}
            onClose={() => setShowTemplates(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Email List Component
function EmailList({ contactId, onCompose }: { contactId: number; onCompose: () => void }) {
  const [emails, setEmails] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchEmails = async () => {
    try {
      const response = await fetch(`/api/crm/communications/emails?contactId=${contactId}`);
      if (response.ok) {
        const data = await response.json();
        setEmails(data.emails);
      }
    } catch (error) {
      console.error('Failed to fetch emails:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, [contactId]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B7FFF]" />
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6">
        <div className="h-16 w-16 rounded-full bg-[#2B7FFF]/10 flex items-center justify-center mb-4">
          <Mail className="h-8 w-8 text-[#2B7FFF]" />
        </div>
        <h3 className="text-lg font-medium">No emails yet</h3>
        <p className="text-muted-foreground text-sm mt-1">Send your first email to this contact</p>
        <Button onClick={onCompose} className="mt-4 bg-[#2B7FFF] hover:bg-[#1A5FCC]">
          <Plus className="h-4 w-4 mr-2" />
          Compose Email
        </Button>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-6 space-y-3">
        {emails.map((email, index) => (
          <motion.div
            key={email.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card className="hover:shadow-md transition-shadow cursor-pointer group">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium truncate">{email.subject}</h4>
                      <Badge
                        variant={email.status === 'sent' ? 'default' : 'secondary'}
                        className={cn(
                          'text-xs',
                          email.status === 'sent' && 'bg-green-500/10 text-green-600 dark:text-green-400'
                        )}
                      >
                        {email.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {email.body}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      <span>{new Date(email.created_at).toLocaleDateString()}</span>
                      {email.open_count > 0 && (
                        <span className="flex items-center gap-1 text-green-600">
                          <Eye className="h-3 w-3" />
                          Opened {email.open_count} time{email.open_count > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </ScrollArea>
  );
}

// Call List Component
function CallList({ contactId, onLogCall }: { contactId: number; onLogCall: () => void }) {
  const [calls, setCalls] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCalls = async () => {
    try {
      const response = await fetch(`/api/crm/communications/calls?contactId=${contactId}`);
      if (response.ok) {
        const data = await response.json();
        setCalls(data.calls);
      }
    } catch (error) {
      console.error('Failed to fetch calls:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCalls();
  }, [contactId]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B7FFF]" />
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6">
        <div className="h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
          <Phone className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-lg font-medium">No calls yet</h3>
        <p className="text-muted-foreground text-sm mt-1">Log your first call with this contact</p>
        <Button onClick={onLogCall} className="mt-4 bg-green-600 hover:bg-green-700">
          <Plus className="h-4 w-4 mr-2" />
          Log Call
        </Button>
      </div>
    );
  }

  const getOutcomeIcon = (outcome: string) => {
    switch (outcome) {
      case 'completed':
        return <Badge className="bg-green-500/10 text-green-600">Completed</Badge>;
      case 'no_answer':
        return <Badge variant="secondary">No Answer</Badge>;
      case 'voicemail':
        return <Badge variant="secondary">Voicemail</Badge>;
      case 'scheduled':
        return <Badge className="bg-blue-500/10 text-blue-600">Scheduled</Badge>;
      default:
        return <Badge variant="secondary">{outcome}</Badge>;
    }
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-6 space-y-3">
        {calls.map((call, index) => (
          <motion.div
            key={call.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'h-10 w-10 rounded-full flex items-center justify-center',
                        call.direction === 'outbound'
                          ? 'bg-blue-500/10 text-blue-600'
                          : 'bg-green-500/10 text-green-600'
                      )}
                    >
                      <Phone className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium capitalize">{call.direction} Call</span>
                        {getOutcomeIcon(call.outcome)}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{call.phone_number}</span>
                        <span>•</span>
                        <span>
                          {Math.floor(call.duration / 60)}m {call.duration % 60}s
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(call.created_at).toLocaleDateString()}
                  </span>
                </div>
                {call.notes && (
                  <p className="text-sm text-muted-foreground mt-3 pt-3 border-t">
                    {call.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </ScrollArea>
  );
}

export default CommunicationHub;
